/**
 * The tier table of "The More You Buy, The More You Save": a header row ("Qty" or "Quantity",
 * by variant, with the unit; "Price, $ / <unit>") and one row per tier with its quantity range
 * and price. Used by both
 * views in this folder and by the product page's version
 * (`components/Product/ProductDetailMoreYouBuy/MoreYouBuyMoreYouSaveDesktop.tsx`,
 * `MoreYouBuyMoreYouSaveMobile.tsx`).
 *
 * Shows the tiers it is given as is; which tiers exist and their prices come from the Prices API
 * through the caller (see `MoreYouBuyMoreYouSave.tsx`).
 */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
	DiscountPriceModel,
	displayPrice,
	getDisplayUom,
	MatchingProduct,
	Product,
	ProductSearchModel,
} from "@ryerson/frontend.common";
import { Flex } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import React from "react";
import { MoreYouBuyMoreYouSaveContentProps } from "./helpers/MoreYouBuyMoreYouSaveContent";

/** Props of {@link MoreYouBuyMoreYouSaveRows}. */
export interface MoreYouBuyMoreYouSaveRowsProps {
	/** "dark" = gray header bg + white text + "Qty" label (default, used by popover/inline on white card)
	 *  "light" = white header bg + secondaryBrand text + "Quantity" label (used by mobile slide-out) */
	variant?: "dark" | "light";
	/** Override color for the quantity column values. Defaults to theme.colors.primary.primaryBrand */
	qtyColor?: string;
	/** Override color for the price column values. Defaults to theme.colors.primary.secondaryBrand */
	priceColor?: string;
	content: MoreYouBuyMoreYouSaveContentProps;
	/** Its `form` picks the unit wording in the headers (`getDisplayUom`). */
	productDetail: Product | ProductSearchModel | MatchingProduct;
	quantityUom: string;
	priceUom: string;
	/**
	 * Tiers in display order: `start` / `end` in the quantity unit (no `end` means open-ended),
	 * `price` per price unit.
	 */
	filteredDiscountedPrice: DiscountPriceModel[];
}

const ColumnHeader = styled(Flex)`
	padding: 10px;
	border-radius: 2px;
	${(props: any) => {
		const { bgColor, textColor, borderColor } = props;
		return css`
			background: ${bgColor};
			color: ${textColor};
			${borderColor ? `border-bottom: 1px solid ${borderColor};` : ""}
		`;
	}}
`;

const RowContent = styled(Flex)`
	margin: 10px 10px;
	padding: 0px 10px;
	width: 100%;
`;

/**
 * Renders the tier table. A tier reads "start+" when it has no `end`, "start" when `start`
 * equals `end`, and "start-end" otherwise. The price is rounded to cents and formatted as US
 * currency with its first character (the "$") removed; the header reads "$ / <unit>".
 */
const MoreYouBuyMoreYouSaveRows: React.FC<MoreYouBuyMoreYouSaveRowsProps> = ({
	variant = "dark",
	qtyColor,
	priceColor,
	content,
	productDetail,
	quantityUom,
	priceUom,
	filteredDiscountedPrice,
}) => {
	const { theme } = useTheme();
	const { white, gray, primaryBrand, secondaryBrand } = theme.colors.primary;

	const isDark = variant === "dark";
	const headerBg = isDark ? gray : "transparent";
	const headerTextColor = isDark ? white : secondaryBrand;
	const headerBorderColor = isDark ? undefined : primaryBrand;
	const qtyLabel = isDark ? content.qty : content.quantity;
	const resolvedQtyColor = qtyColor ?? primaryBrand;
	const resolvedPriceColor = priceColor ?? secondaryBrand;

	return (
		<Flex direction="column"   >
			<ColumnHeader
				justifyContent="space-between"
				css={!isDark ? { margin: "0px 20px", padding: "10px 0px" } : undefined}
				{...{
					bgColor: headerBg,
					textColor: headerTextColor,
					borderColor: headerBorderColor,
				}}
			>
				<Typography color={headerTextColor} size="xs">
					{`${qtyLabel}, ${getDisplayUom(productDetail.form, quantityUom, "quantity")}`}
				</Typography>
				<Typography color={headerTextColor} size="xs">
					{`${content.price}, $ / ${getDisplayUom(productDetail.form, priceUom, "price")}`}
				</Typography>
			</ColumnHeader>
			{filteredDiscountedPrice.map((row: DiscountPriceModel, index: number) => (
				<Flex key={index}>
					<RowContent justifyContent="space-between" alignItems="center">
						<Flex alignItems="center">
							<Typography color={resolvedQtyColor} size="sm">
								{row?.end
									? row?.start === row?.end
										? row?.start
										: `${row.start}-${row.end}`
									: `${row.start}+`}
							</Typography>
						</Flex>
						<Flex>
							<Typography color={resolvedPriceColor} size="sm">
								{`${displayPrice(Number(Number(row.price).toFixed(2))).substring(1)}`}
							</Typography>
						</Flex>
					</RowContent>
				</Flex>
			))}
		</Flex>
	);
};

export default MoreYouBuyMoreYouSaveRows;
